import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid'

import { Loading } from 'components/Loading'
import showMessage from 'components/showMessage'
import { useFirebaseApp } from 'contexts/FirebaseApp'
import { IconClass, InputClass, SmallButtonClass } from 'contexts/style'

type OptionsState = {
  shippingMethod: string[]
  shippingNote: string[]
}

const FIELD_CONFIGS: { key: keyof OptionsState; label: string; }[] = [
  {
    key: 'shippingMethod',
    label: '発送方法',
  },
  {
    key: 'shippingNote',
    label: '備考(発送用)',
  },
]

const DEFAULT_OPTIONS: OptionsState = {
  shippingMethod: [],
  shippingNote: [],
}

const ReportingSettings = () => {
  const { functions } = useFirebaseApp()
  const [optionsState, setOptionsState] = useState<OptionsState>(DEFAULT_OPTIONS)
  const [loading, setLoading] = useState(true)
  const [savingField, setSavingField] = useState<keyof OptionsState | null>(null)
  const [newValues, setNewValues] = useState<Record<keyof OptionsState, string>>({
    shippingMethod: '',
    shippingNote: '',
  })
  const [editingState, setEditingState] = useState<{
    field: keyof OptionsState | null
    index: number | null
    value: string
  }>({
    field: null,
    index: null,
    value: '',
  })

  const resetEditingState = () =>
    setEditingState({
      field: null,
      index: null,
      value: '',
    })

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const result = (await functions([
        'mongo/client',
        { collection: 'settings', findOne: { filter: { _id: '1' } } },
      ])) as KV | null
      const fetchedOptions = (result?.options || {}) as OptionsState
      setOptionsState({
        shippingMethod: fetchedOptions.shippingMethod || [],
        shippingNote: fetchedOptions.shippingNote || [],
      })
    } catch (err) {
      if (err instanceof Error) {
        await showMessage(`設定の取得に失敗しました。\r\n${err.message}`, { error: true })
      }
    } finally {
      setLoading(false)
    }
  }, [functions])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const persistField = useCallback(
    async (field: keyof OptionsState, values: string[], successMessage: string) => {
      setSavingField(field)
      try {
        await functions([
          'mongo/client',
          {
            collection: 'settings',
            findOneAndUpdate: {
              filter: { _id: '1' },
              update: { $set: { [`options.${field}`]: values } },
              options: { upsert: true },
            },
          },
        ])
        setOptionsState(prev => ({ ...prev, [field]: values }))
        resetEditingState()
        setNewValues(prev => ({ ...prev, [field]: '' }))
        await showMessage(successMessage)
      } catch (err) {
        if (err instanceof Error) {
          await showMessage(`設定の更新に失敗しました。\r\n${err.message}`, { error: true })
        }
      } finally {
        setSavingField(current => (current === field ? null : current))
      }
    },
    [functions]
  )

  const handleAdd = async (field: keyof OptionsState, e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const value = newValues[field].trim()
    if (!value) {
      await showMessage('空の値は追加できません。', { error: true })
      return
    }
    if (optionsState[field].includes(value)) {
      await showMessage('同じ値が既に登録されています。', { error: true })
      return
    }
    await persistField(field, [...optionsState[field], value], '項目を追加しました。')
  }

  const handleDelete = async (field: keyof OptionsState, index: number) => {
    const target = optionsState[field][index]
    if (
      !(await showMessage(`「${target}」を削除します。よろしいですか？`, {
        confirm: true,
      }))
    ) {
      return
    }
    const nextValues = optionsState[field].filter((_, idx) => idx !== index)
    await persistField(field, nextValues, '項目を削除しました。')
  }

  const beginEdit = (field: keyof OptionsState, index: number, currentValue: string) => {
    setEditingState({
      field,
      index,
      value: currentValue,
    })
  }

  const handleEditSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (editingState.field == null || editingState.index == null) return
    const nextValue = editingState.value.trim()
    if (!nextValue) {
      await showMessage('空の値は保存できません。', { error: true })
      return
    }
    const existing = optionsState[editingState.field]
    if (
      existing.some((value, idx) => value === nextValue && idx !== editingState.index)
    ) {
      await showMessage('同じ値が既に登録されています。', { error: true })
      return
    }
    const nextValues = existing.map((value, idx) =>
      idx === editingState.index ? nextValue : value
    )
    await persistField(editingState.field, nextValues, '項目を更新しました。')
  }

  const isEditing = (field: keyof OptionsState, index: number) =>
    editingState.field === field && editingState.index === index

  const isBusy = useMemo(() => loading || savingField !== null, [loading, savingField])

  if (loading) {
    return <Loading full />
  }

  return (
    <div className="space-y-6">
      <div className="bg-theme-200">
        <div className="mx-auto max-w-5xl px-4 py-4 sm:flex sm:items-center sm:justify-between">
          <div>
            <span className="text-2xl font-semibold text-green-800">レポート管理</span>
            <p className="text-sm text-gray-700">
              ※変更内容は即時反映されます。
            </p>
          </div>
        </div>
      </div>
      <div className="mx-auto grid max-w-5xl gap-6 px-4 lg:grid-cols-2">
        {FIELD_CONFIGS.map(({ key, label }) => (
          <section key={key} className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-theme-800">{label}</h2>
            </div>
            <form className="flex gap-2 items-center" onSubmit={e => handleAdd(key, e)}>
              <input
                type="text"
                className={`${InputClass} flex-1`}
                value={newValues[key]}
                onChange={e => {
                  const val = e.currentTarget.value
                  setNewValues(prev => ({
                    ...prev,
                    [key]: val,
                  }))
                }}
                placeholder={`${label}を入力`}
                disabled={savingField === key}
              />
              <button
                type="submit"
                className={SmallButtonClass}
                disabled={savingField === key}
              >
                追加
              </button>
            </form>
            <ul className="mt-4 space-y-2">
              {optionsState[key]?.length ? (
                optionsState[key].map((value, index) => (
                  <li
                    key={`${key}-${value}-${index}`}
                    className="flex flex-wrap items-center gap-2 rounded border border-gray-100 bg-theme-50 px-3 py-2"
                  >
                    {isEditing(key, index) ? (
                      <form className="flex flex-1 gap-2 items-center" onSubmit={handleEditSubmit}>
                        <input
                          type="text"
                          className={`${InputClass} flex-1`}
                          value={editingState.value}
                          onChange={e => {
                            const val = e.currentTarget.value
                            setEditingState(prev => ({ ...prev, value: val }))
                          }}
                          disabled={savingField === key}
                        />
                        <button
                          type="submit"
                          className={SmallButtonClass}
                          disabled={savingField === key}
                        >
                          保存
                        </button>
                        <button
                          type="button"
                          className={SmallButtonClass}
                          onClick={resetEditingState}
                          disabled={savingField === key}
                        >
                          キャンセル
                        </button>
                      </form>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-gray-900">{value}</span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className={IconClass}
                            onClick={() => beginEdit(key, index, value)}
                            disabled={savingField === key || isBusy}
                          >
                            <PencilSquareIcon />
                          </button>
                          <button
                            type="button"
                            className={IconClass}
                            onClick={() => handleDelete(key, index)}
                            disabled={savingField === key || isBusy}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))
              ) : (
                <li className="rounded border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-500">
                  {label}はまだ登録されていません。
                </li>
              )}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}

export default ReportingSettings

